import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, FlatList, StyleSheet, TouchableOpacity,
  Share, ScrollView, Alert,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { ProfileStackParamList } from '../../navigation/AppNavigator';
import { appLog, type LogEntry } from '../../utils/logger';

type Props = NativeStackScreenProps<ProfileStackParamList, 'Logs'>;

const LEVEL_COLOR: Record<string, string> = {
  info: '#1a73e8',
  warn: '#f4a100',
  error: '#ea4335',
};

export default function LogsScreen(_props: Props) {
  const [logs, setLogs] = useState<LogEntry[]>(() => appLog.getAll());
  const [filter, setFilter] = useState<'all' | 'warn' | 'error'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    // Subscribe to new log entries
    const unsub = appLog.subscribe((entry) => {
      setLogs((prev) => [...prev, entry]);
    });
    return unsub;
  }, []);

  const handleExport = useCallback(() => {
    const text = appLog
      .getAll()
      .map((e) => `[${e.timestamp}] [${e.level.toUpperCase()}] ${e.message}${e.data ? '\n' + e.data : ''}`)
      .join('\n\n');
    Share.share({ message: text, title: 'Landmark Run Logs' });
  }, []);

  const handleClear = useCallback(() => {
    Alert.alert('Clear Logs', 'Delete all logs?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Clear', style: 'destructive', onPress: () => { appLog.clear(); setLogs([]); } },
    ]);
  }, []);

  const visible = filter === 'all' ? logs : logs.filter((e) => e.level === filter);
  const reversed = [...visible].reverse(); // newest first

  return (
    <View style={styles.container}>
      {/* Filter bar */}
      <View style={styles.filterRow}>
        {(['all', 'warn', 'error'] as const).map((f) => (
          <TouchableOpacity
            key={f}
            style={[styles.filterBtn, filter === f && styles.filterBtnActive]}
            onPress={() => setFilter(f)}
          >
            <Text style={[styles.filterText, filter === f && styles.filterTextActive]}>
              {f === 'all' ? 'All' : f === 'warn' ? '⚠ Warn' : '✖ Error'}
            </Text>
          </TouchableOpacity>
        ))}
        <View style={{ flex: 1 }} />
        <TouchableOpacity style={styles.actionBtn} onPress={handleExport}>
          <Text style={styles.actionText}>Export</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.actionBtn, styles.clearBtn]} onPress={handleClear}>
          <Text style={styles.actionText}>Clear</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.count}>{visible.length} entries (newest first)</Text>

      <FlatList
        data={reversed}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <TouchableOpacity onPress={() => setExpanded(expanded === item.id ? null : item.id)}>
            <View style={styles.entry}>
              <View style={styles.entryHeader}>
                <Text style={[styles.levelBadge, { backgroundColor: LEVEL_COLOR[item.level] }]}>
                  {item.level.toUpperCase()}
                </Text>
                <Text style={styles.ts}>{item.timestamp.slice(11, 23)}</Text>
                {item.data && <Text style={styles.expandHint}>▼</Text>}
              </View>
              <Text style={styles.message}>{item.message}</Text>
              {expanded === item.id && item.data && (
                <ScrollView horizontal style={styles.dataScroll}>
                  <Text style={styles.data}>{item.data}</Text>
                </ScrollView>
              )}
            </View>
          </TouchableOpacity>
        )}
        ItemSeparatorComponent={() => <View style={styles.sep} />}
        contentContainerStyle={{ paddingBottom: 40 }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8f9fa' },
  filterRow: {
    flexDirection: 'row', alignItems: 'center', padding: 8, gap: 6,
    backgroundColor: '#fff', borderBottomWidth: 1, borderColor: '#e0e0e0',
  },
  filterBtn: {
    paddingHorizontal: 10, paddingVertical: 6,
    borderRadius: 8, backgroundColor: '#f0f0f0',
  },
  filterBtnActive: { backgroundColor: '#1a73e8' },
  filterText: { fontSize: 12, fontWeight: '600', color: '#555' },
  filterTextActive: { color: '#fff' },
  actionBtn: {
    paddingHorizontal: 10, paddingVertical: 6,
    borderRadius: 8, backgroundColor: '#1a73e8',
  },
  clearBtn: { backgroundColor: '#ea4335' },
  actionText: { color: '#fff', fontSize: 12, fontWeight: '600' },
  count: { fontSize: 11, color: '#888', padding: 8, paddingBottom: 4 },
  entry: { backgroundColor: '#fff', padding: 10 },
  entryHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 },
  levelBadge: {
    color: '#fff', fontSize: 10, fontWeight: '700',
    paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
  },
  ts: { fontSize: 11, color: '#888', fontFamily: 'monospace' },
  expandHint: { fontSize: 11, color: '#aaa' },
  message: { fontSize: 13, color: '#222' },
  dataScroll: { maxHeight: 120, marginTop: 6 },
  data: {
    fontSize: 11, color: '#555', fontFamily: 'monospace',
    backgroundColor: '#f4f4f4', padding: 6, borderRadius: 4,
  },
  sep: { height: 1, backgroundColor: '#f0f0f0' },
});
